import { useCallback, useEffect, useMemo, useState } from 'react';
import PageShell from '../../../components/layout/PageShell';
import { useAuth } from '../../../context/AuthContext';
import { copyTextToClipboard } from '../../../utils/clipboard';
import {
  fetchMatchStreamEvents,
  getBroadcastHistory,
  getStreamingDashboard,
  setPrimaryBroadcastReplay,
  takeoverStreamControl,
} from '../../../services/matchService';
import {
  canTakeover,
  normalizeDashboardItem,
} from '../utils/streamingDashboardUtils';
import BroadcastControlCenter, { HealthBadge, QualityBadge, ReplayBadge } from '../../broadcast/components/BroadcastControlCenter';

const severityBadgeClass = {
  info: 'text-bg-info',
  warning: 'text-bg-warning text-dark',
  critical: 'text-bg-danger',
};

const modeLabel = {
  DIRECT_MOBILE_TO_YOUTUBE: 'Direct mobile',
  OBS_TO_YOUTUBE: 'OBS',
  LOCAL_MAC_TEST: 'Local test',
  DIRECT_MOBILE: 'Direct mobile',
  OBS: 'OBS',
  LOCAL_MAC_TESTING: 'Local test',
};

const StreamingDashboardPage = () => {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [filters, setFilters] = useState({ search: '', status: 'all', source: 'all', unhealthy: false, provider: 'all', tournamentId: '', operator: '', date: '', health: 'all', quality: 'all' });
  const [replayFilters, setReplayFilters] = useState({ search: '', streamType: 'all' });
  const [logModal, setLogModal] = useState({ open: false, matchName: '', items: [] });
  const [controlCenter, setControlCenter] = useState({ open: false, row: null, history: null });

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const payload = await getStreamingDashboard({ token, limit: 250, ...filters });
      setItems((payload?.items || []).map(normalizeDashboardItem));
    } catch (err) {
      setError(err.message || 'Unable to load streaming dashboard.');
    } finally {
      setLoading(false);
    }
  }, [token, filters]);

  useEffect(() => { load(); }, [load]);

  const unhealthyCount = useMemo(() => items.filter((row) => (row.alerts || []).some((a) => a.severity !== 'info')).length, [items]);

  const replayItems = useMemo(() => items
    .filter((row) => !row.isLive && row.replay?.url)
    .filter((row) => {
      if (replayFilters.streamType !== 'all' && row.streamType !== replayFilters.streamType) return false;
      if (!replayFilters.search) return true;
      return `${row.matchName} ${row.tournamentName}`.toLowerCase().includes(replayFilters.search.toLowerCase());
    }), [items, replayFilters]);

  const notifications = useMemo(() => items
    .flatMap((row) => (row.alerts || []).map((alert) => ({ row, alert })))
    .sort((a, b) => (a.alert.severity === 'critical' ? -1 : 1)), [items]);

  const tournamentGroups = useMemo(() => {
    const map = new Map();
    items.forEach((row) => {
      const key = row.tournamentId || 'unknown';
      if (!map.has(key)) map.set(key, { tournamentName: row.tournamentName || 'Unknown tournament', rows: [] });
      map.get(key).rows.push(row);
    });
    return Array.from(map.entries());
  }, [items]);

  const handleTakeover = async (row) => {
    try {
      setActionMessage('');
      await takeoverStreamControl({ token, matchId: row.matchId, tournamentId: row.tournamentId, streamType: row.streamType || 'match' });
      setActionMessage(`Taken over stream control for ${row.matchName}.`);
      await load();
    } catch (err) {
      setActionMessage(err.message || 'Takeover failed.');
    }
  };

  const handleViewLogs = async (row) => {
    try {
      const payload = await fetchMatchStreamEvents({ token, matchId: row.matchId, tournamentId: row.tournamentId, streamType: row.streamType || 'match' });
      setLogModal({ open: true, matchName: row.matchName, items: payload?.items || [] });
    } catch (err) {
      setActionMessage(err.message || 'Unable to load stream logs.');
    }
  };

  const handleOpenControlCenter = async (row) => {
    setControlCenter({ open: true, row, history: null });
    try {
      const history = await getBroadcastHistory({ token, broadcastType: row.streamType || 'match', entityId: row.matchId, entityType: row.streamType === 'match' ? 'match' : 'tournament' });
      setControlCenter({ open: true, row, history });
    } catch (err) {
      setActionMessage(err.message || 'Unable to load broadcast history.');
    }
  };

  const handleSetPrimaryReplay = async (session) => {
    await setPrimaryBroadcastReplay({ token, sessionId: session.sessionId });
    if (controlCenter.row) await handleOpenControlCenter(controlCenter.row);
  };

  const handleCopy = async (value, label) => {
    try {
      await copyTextToClipboard(value);
      setActionMessage(`${label} copied.`);
    } catch (err) {
      setActionMessage(err.message || 'Copy failed.');
    }
  };

  return (
    <PageShell>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 className="h4 mb-0">Streaming dashboard</h1>
          <div className="small text-muted">{items.length} streams • {unhealthyCount} unhealthy</div>
        </div>
        <button type="button" className="btn btn-outline-primary btn-sm" onClick={load} disabled={loading}>Refresh</button>
      </div>

      <div className="card mb-3">
        <div className="card-body py-2">
          <div className="row g-2">
            <div className="col-md-4">
              <input className="form-control form-control-sm" placeholder="Search match/tournament" value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} />
            </div>
            <div className="col-md-3">
              <select className="form-select form-select-sm" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
                <option value="all">All statuses</option>
                <option value="preparing">Preparing</option>
                <option value="live">Live</option>
                <option value="ended">Ended</option>
                <option value="failed">Failed</option>
                <option value="replay_ready">Replay Ready</option>
              </select>
            </div>
            <div className="col-md-3">
              <select className="form-select form-select-sm" value={filters.source} onChange={(e) => setFilters((f) => ({ ...f, source: e.target.value }))}>
                <option value="all">All sources</option>
                <option value="mobile">Direct mobile</option>
                <option value="obs">OBS</option>
              </select>
            </div>
            <div className="col-md-2 d-flex align-items-center">
              <div className="form-check">
                <input id="unhealthyOnly" className="form-check-input" type="checkbox" checked={filters.unhealthy} onChange={(e) => setFilters((f) => ({ ...f, unhealthy: e.target.checked }))} />
                <label className="form-check-label small" htmlFor="unhealthyOnly">Unhealthy only</label>
              </div>
            </div>
            <div className="col-md-2"><input className="form-control form-control-sm" placeholder="Tournament" value={filters.tournamentId} onChange={(e) => setFilters((f) => ({ ...f, tournamentId: e.target.value }))} /></div>
            <div className="col-md-2"><input className="form-control form-control-sm" placeholder="Operator" value={filters.operator} onChange={(e) => setFilters((f) => ({ ...f, operator: e.target.value }))} /></div>
            <div className="col-md-2"><input className="form-control form-control-sm" type="date" value={filters.date} onChange={(e) => setFilters((f) => ({ ...f, date: e.target.value }))} /></div>
            <div className="col-md-2"><select className="form-select form-select-sm" value={filters.health} onChange={(e) => setFilters((f) => ({ ...f, health: e.target.value }))}><option value="all">All health</option><option value="healthy">Healthy</option><option value="warning">Warning</option><option value="failed">Failed</option></select></div>
            <div className="col-md-2"><select className="form-select form-select-sm" value={filters.quality} onChange={(e) => setFilters((f) => ({ ...f, quality: e.target.value }))}><option value="all">All quality</option><option value="good">Good</option><option value="warning">Warning</option><option value="poor">Poor</option></select></div>
          </div>
        </div>
      </div>

      {actionMessage && <div className="alert alert-info py-2">{actionMessage}</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="table-responsive mb-4">
        <table className="table table-sm align-middle">
          <thead>
            <tr>
              <th>Stream</th><th>Status</th><th>Health</th><th>Quality</th><th>Mode</th><th>Alerts</th><th>Analytics</th><th>Replay</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && items.map((row) => (
              <tr key={`${row.streamType}-${row.matchId}`}>
                <td>
                  <div>{row.matchName} {row.streamType !== 'match' ? <span className="badge text-bg-primary ms-1">{row.streamType}</span> : null}</div>
                  <div className="small text-muted">{row.tournamentName}</div>
                </td>
                <td>{row.isLive ? <span className="badge text-bg-danger">LIVE</span> : <span className="badge text-bg-secondary">COMPLETED/OFFLINE</span>} {row.viewerReady ? <span className="badge text-bg-success ms-1">Viewer Ready</span> : null}</td>
                <td><HealthBadge status={row.healthStatus || row.health?.status} score={row.healthScore ?? row.health?.score} /></td>
                <td><QualityBadge level={row.qualityLevel || row.quality?.level} score={row.qualityScore ?? row.quality?.score} /></td>
                <td>{modeLabel[row.streamingMode] || row.streamingMode}</td>
                <td>
                  {(row.alerts || []).length === 0 ? '—' : (
                    <div className="d-flex flex-column gap-1">
                      {row.alerts.map((alert) => <span key={`${row.matchId}-${alert.code}`} className={`badge ${severityBadgeClass[alert.severity] || 'text-bg-secondary'}`}>{alert.message}</span>)}
                    </div>
                  )}
                </td>
                <td>
                  <div className="small">Live: {Math.round((row.analytics?.liveDurationMs || 0) / 60000)} min</div>
                  <div className="small">Reconnects: {row.analytics?.reconnectAttempts || 0} / failures: {row.analytics?.reconnectFailures || 0}</div>
                </td>
                <td><ReplayBadge status={row.replay?.status} available={Boolean(row.replay?.url)} /></td>
                <td>
                  <div className="d-flex flex-wrap gap-1">
                    {row.watchLink && <a href={row.watchLink} target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-sm">Watch live</a>}
                    {row.replay?.url && <a href={row.replay.url} target="_blank" rel="noreferrer" className="btn btn-outline-success btn-sm">Replay</a>}
                    {row.watchLink && <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => handleCopy(row.watchLink, 'Watch link')}>Copy watch</button>}
                    <button type="button" className="btn btn-outline-dark btn-sm" onClick={() => handleViewLogs(row)}>Logs</button>
                    <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => handleOpenControlCenter(row)}>Broadcast History</button>
                    {canTakeover(row) && <button type="button" className="btn btn-warning btn-sm" onClick={() => handleTakeover(row)}>Take over</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="row g-3">
        <div className="col-lg-7">
          <div className="card h-100">
            <div className="card-header py-2 d-flex gap-2 align-items-center">
              <strong>Replay archive</strong>
              <input className="form-control form-control-sm" placeholder="Search replays" value={replayFilters.search} onChange={(e) => setReplayFilters((f) => ({ ...f, search: e.target.value }))} />
              <select className="form-select form-select-sm" style={{ maxWidth: 200 }} value={replayFilters.streamType} onChange={(e) => setReplayFilters((f) => ({ ...f, streamType: e.target.value }))}>
                <option value="all">All types</option>
                <option value="match">Match</option>
                <option value="live_auction">Live auction</option>
                <option value="online_auction">Online auction</option>
              </select>
            </div>
            <div className="card-body py-2">
              {replayItems.length === 0 && <div className="text-muted small">No completed stream replays available yet.</div>}
              {replayItems.length > 0 && replayItems.map((row) => (
                <div className="border rounded p-2 mb-2" key={`${row.streamType}-${row.matchId}-replay`}>
                  <div className="fw-semibold">{row.matchName}</div>
                  <div className="small text-muted">{row.streamType} • Ended: {row.replay?.updatedAt ? new Date(row.replay.updatedAt).toLocaleString() : '—'}</div>
                  <div className="d-flex gap-2 mt-2">
                    <a href={row.replay.url} target="_blank" rel="noreferrer" className="btn btn-outline-success btn-sm">Watch replay</a>
                    <button type="button" className="btn btn-outline-dark btn-sm" onClick={() => handleViewLogs(row)}>View logs</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="col-lg-5">
          <div className="card h-100">
            <div className="card-header py-2"><strong>Notification center</strong></div>
            <div className="card-body py-2">
              {notifications.length === 0 ? <div className="text-muted small">No alerts right now.</div> : notifications.map(({ row, alert }, idx) => (
                <div key={`${row.matchId}-${alert.code}-${idx}`} className="border rounded p-2 mb-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className={`badge ${severityBadgeClass[alert.severity] || 'text-bg-secondary'}`}>{alert.severity}</span>
                    <span className="badge text-bg-light border">{row.streamType}</span>
                  </div>
                  <div className="fw-semibold mt-1">{alert.message}</div>
                  <div className="small text-muted">{row.matchName}</div>
                  <div className="d-flex gap-2 mt-2">
                    {row.watchLink ? <a href={row.watchLink} target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-sm">Watch</a> : null}
                    <button type="button" className="btn btn-outline-dark btn-sm" onClick={() => handleViewLogs(row)}>Logs</button>
                    <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => handleOpenControlCenter(row)}>Broadcast History</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="card mt-3">
        <div className="card-header py-2"><strong>Tournament operations</strong></div>
        <div className="card-body py-2">
          {tournamentGroups.map(([tournamentId, group]) => (
            <div key={tournamentId} className="border rounded p-2 mb-2">
              <div className="fw-semibold">{group.tournamentName}</div>
              <div className="small text-muted mb-2">{group.rows.length} streams</div>
              <div className="d-flex flex-wrap gap-2">
                {group.rows.map((row) => (
                  <span key={`${row.streamType}-${row.matchId}`} className="badge text-bg-light border">
                    {row.streamType} • {row.isLive ? 'Live' : 'Offline'} • alerts {(row.alerts || []).length}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {controlCenter.open && (
        <div className="modal d-block" tabIndex="-1" role="dialog" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable" role="document"><div className="modal-content"><div className="modal-header"><h5 className="modal-title">Broadcast Control Center • {controlCenter.row?.matchName}</h5><button type="button" className="btn-close" onClick={() => setControlCenter({ open: false, row: null, history: null })} /></div><div className="modal-body">
            <BroadcastControlCenter operations={controlCenter.row?.operations || controlCenter.row || {}} history={controlCenter.history || { sessions: [] }} onSetPrimary={handleSetPrimaryReplay} />
          </div></div></div>
        </div>
      )}

      {logModal.open && (
        <div className="modal d-block" tabIndex="-1" role="dialog" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Stream logs • {logModal.matchName}</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setLogModal({ open: false, matchName: '', items: [] })} />
              </div>
              <div className="modal-body">
                {logModal.items.length === 0 && <div className="text-muted">No logs available.</div>}
                {logModal.items.map((event) => (
                  <div key={event._id} className="border-bottom pb-2 mb-2">
                    <div className="fw-semibold">{event.type}</div>
                    <div className="small">{event.message || '—'}</div>
                    <div className="small text-muted">{new Date(event.createdAt).toLocaleString()}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
};

export default StreamingDashboardPage;
